/* React and Next */
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';
import { useRouter } from 'next/router';
import { useCookies } from 'react-cookie';

/* Material UI */
import {
  Grid, darken, IconButton, useTheme,
} from '@material-ui/core';

import { makeStyles } from '@material-ui/core/styles';

/* Facebook & Google */
import FacebookLogin, {
  ReactFacebookLoginInfo,
  // @ts-ignore
} from 'react-facebook-login/dist/facebook-login-render-props';
import GoogleLogin, {
  GoogleLoginResponse,
  GoogleLoginResponseOffline,
} from 'react-google-login';

/* Icons */
import { FacebookF, GoogleIcon } from '@/components/ui-components/SVGIcons';

/* Local imports */

import { RootState } from '@/store/rootReducer';
import { AppDispatch } from '@/store/store';
import {
  getUserData,
  signInFacebook,
  signInGoogle,
} from '@/features/auth/api/slice';

/* Styles */
const useStyles = makeStyles((theme) => ({
  iconButton: {
    margin: 0,
    padding: 0,
    backgroundColor: theme.palette.primary.extralight,
    '&:hover': {
      backgroundColor: theme.palette.primary.superlight,
    },
  },
  loginIcons: {
    borderRadius: '25px',
    height: '48px',
    width: '48px',
    backgroundColor: 'transparent',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

const SocialNetwork = () => {
  const classes = useStyles();
  const { push, query } = useRouter();
  const dispatch = useDispatch() as AppDispatch;
  const { auth } = useSelector((state: RootState) => state);
  const theme = useTheme();

  const [cookies, setCookie] = useCookies();

  /* Log With Facebook */
  const responseFacebook = (response: ReactFacebookLoginInfo) => {
    dispatch(
      signInFacebook({
        token: response.accessToken,
        code: response.signedRequest,
      }),
    )
      .then(unwrapResult)
      .then((res) => {
        if (res.key) {
          // store cookie if we can
          if (cookies.allow_cookies) {
            setCookie('token', res.key, {
              path: '/',
              expires: new Date('2099-12-31'),
              secure: true,
              sameSite: 'lax',
            });
            // otherwise just use localStorage
          } else {
            localStorage.setItem('token', res.key);
          }
        }
        dispatch(getUserData()).then(() => {
          push((query.next as string) || '/markets/offerings');
        });
      });
  };

  /* Log With Google */
  const responseGoogle = (
    response: GoogleLoginResponse | GoogleLoginResponseOffline,
  ) => {
    // check if reponse is of type "GoogleLoginResponse"
    // GoogleLoginResponseOffline doesn't work here
    if ('googleId' in response) {
      dispatch(
        signInGoogle({ token: response.accessToken, code: response.tokenId }),
      )
        .then(unwrapResult)
        .then((res) => {
          if (res.key) {
            // store cookie if we can
            if (cookies.allow_cookies) {
              setCookie('token', res.key, {
                path: '/',
                expires: new Date('2099-12-31'),
                secure: true,
                sameSite: 'lax',
              });
              // otherwise just use localStorage
            } else {
              localStorage.setItem('token', res.key);
            }
          }
          dispatch(getUserData()).then(() => {
            push((query.next as string) || '/markets/offerings');
          });
        });
    }
  };

  return (
    <>
      {/* Google Button */}
      <Grid item xs={6} md={6}>
        <Grid container justify="space-around">
          <GoogleLogin
            clientId={process.env.NEXT_PUBLIC_GOOGLE || ''}
            buttonText="Google"
            onSuccess={(
              response: GoogleLoginResponse | GoogleLoginResponseOffline,
            ) => responseGoogle(response)}
            onFailure={responseGoogle}
            disabled={auth.isLoading}
            cookiePolicy="single_host_origin"
            render={(renderProps) => (
              <>
                <IconButton
                  className={classes.iconButton}
                  onClick={renderProps.onClick}
                >
                  <div className={classes.loginIcons}>
                    <GoogleIcon dark={false} />
                  </div>
                </IconButton>
              </>
            )}
          />
        </Grid>
      </Grid>
      {/* Facebook Button */}
      <Grid item xs={6} md={6}>
        <Grid container justify="space-around">
          <FacebookLogin
            isMobile={false}
            disableMobileRedirect
            appId={process.env.NEXT_PUBLIC_FACEBOOK || ''}
            fields="name,email,picture"
            callback={(response: ReactFacebookLoginInfo) => responseFacebook(response)}
            render={(renderProps: any) => (
              <>
                <IconButton
                  className={classes.iconButton}
                  onClick={renderProps.onClick}
                >
                  <div className={classes.loginIcons}>
                    <FacebookF color={theme.palette.primary.main} />
                  </div>
                </IconButton>
              </>
            )}
          />
        </Grid>
      </Grid>
      {/* End Facebook Grid */}
    </>
  );
};

export default SocialNetwork;
